import React, { useState, useEffect } from "react";
import DOMPurify from "dompurify";
import { useTranslation } from "../../i18n/LanguageContext";
import "./css/GettingStarted.css";
import "./css/Common.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faSms,
  faPhone,
  faImage,
  faLink,
  faShareAlt,
  faComments,
  faGraduationCap,
  faBell,
  faShieldAlt,
  faChartLine,
  faUserPlus,
  faUsers,
  faClipboardList,
  faCog,
  faCheckCircle,
  faArrowRight,
  faRocket,
  faThumbtack,
  faQuestionCircle,
  faChevronDown,
  faChevronUp,
} from "@fortawesome/free-solid-svg-icons";
import UserSettingsAPI from "./services/UserSettingsAPI";
import { useSubscription } from "./context/SubscriptionContext";
import InstallAppPanel from "./InstallAppPanel";
import HelpOptionsFooter from "./common/HelpOptionsFooter";

/**
 * GettingStarted Component
 *
 * A guided onboarding page shown to new users after account setup is complete.
 * Designed for low cognitive load — groups features into clear categories,
 * highlights recommended first actions, and tracks which features the user
 * has explored.
 *
 * Graduation logic:
 *   - The user is shown GettingStarted until they've explored at least 3
 *     features (clicked through to them) OR manually clicked "Switch to How Do I".
 *   - Once graduated, their landing page is updated to "How do I...?" and
 *     the Getting Started link in the header is hidden.
 *   - Graduation state is stored in localStorage ("gettingStarted_graduated")
 *     and persisted to the backend via UserSettings.
 */

// ─── Feature categories with items ──────────────────────────────────────────

function buildCategories(t) {
  return [
    {
      id: "protect",
      title: t("gs.category.protect.title"),
      subtitle: t("gs.category.protect.subtitle"),
      // Items without `primary: true` are hidden behind a "show more" toggle
      collapsible: true,
      moreHint: t("gs.category.protect.moreHint"),
      items: [
        {
          id: "email",
          label: t("gs.item.email.label"),
          description: t("gs.item.email.description"),
          icon: faEnvelope,
          action: "Forwarded Emails",
          primary: true,
        },
        {
          id: "text",
          label: t("gs.item.text.label"),
          description: t("gs.item.text.description"),
          icon: faSms,
          action: "Forwarded Texts",
          primary: true,
        },
        {
          id: "phone",
          label: t("gs.item.phone.label"),
          description: t("gs.item.phone.description"),
          icon: faPhone,
          action: "Phone Number Check",
        },
        {
          id: "url",
          label: t("gs.item.url.label"),
          description: t("gs.item.url.description"),
          icon: faLink,
          action: "Link Safety",
        },
        {
          id: "screenshot",
          label: t("gs.item.screenshot.label"),
          description: t("gs.item.screenshot.description"),
          icon: faImage,
          action: "Screenshot Analysis",
        },
        {
          id: "social",
          label: t("gs.item.social.label"),
          description: t("gs.item.social.description"),
          icon: faShareAlt,
          action: "Business/Person Check",
        },
        {
          id: "conversation",
          label: t("gs.item.conversation.label"),
          description: t("gs.item.conversation.description"),
          icon: faComments,
          action: "Text Conversation",
        },
      ],
    },
    {
      id: "learn",
      title: t("gs.category.learn.title"),
      subtitle: t("gs.category.learn.subtitle"),
      items: [
        {
          id: "training",
          label: t("gs.item.training.label"),
          description: t("gs.item.training.description"),
          icon: faGraduationCap,
          action: "Training Center",
          recommended: true,
        },
        {
          id: "alerts",
          label: t("gs.item.alerts.label"),
          description: t("gs.item.alerts.description"),
          icon: faBell,
          action: "Alerts Intelligence Hub",
        },
      ],
    },
    {
      id: "monitor",
      title: t("gs.category.monitor.title"),
      subtitle: t("gs.category.monitor.subtitle"),
      items: [
        {
          id: "score",
          label: t("gs.item.score.label"),
          description: t("gs.item.score.description"),
          icon: faShieldAlt,
          action: "Dashboard",
          recommended: true,
        },
        {
          id: "risk",
          label: t("gs.item.risk.label"),
          description: t("gs.item.risk.description"),
          icon: faChartLine,
          action: "Shield Reports",
        },
      ],
    },
    {
      id: "family",
      title: t("gs.category.family.title"),
      subtitle: t("gs.category.family.subtitle"),
      items: [
        {
          id: "adduser",
          label: t("gs.item.adduser.label"),
          description: t("gs.item.adduser.description"),
          icon: faUserPlus,
          action: "Member User Info:Create",
        },
        {
          id: "manageusers",
          label: t("gs.item.manageusers.label"),
          description: t("gs.item.manageusers.description"),
          icon: faUsers,
          action: "Member User Info",
        },
      ],
    },
    {
      id: "account",
      title: t("gs.category.account.title"),
      subtitle: t("gs.category.account.subtitle"),
      items: [
        {
          id: "plan",
          label: t("gs.item.plan.label"),
          description: t("gs.item.plan.description"),
          icon: faClipboardList,
          action: "Subscriptions",
        },
        {
          id: "settings",
          label: t("gs.item.settings.label"),
          description: t("gs.item.settings.description"),
          icon: faCog,
          action: "Settings",
        },
      ],
    },
  ];
}

function GettingStarted({ setHeroContent }) {
  const { t } = useTranslation();
  const { getMaxUsers } = useSubscription();
  const maxUsers = getMaxUsers();
  // -1 = unlimited; 0 = no plan loaded
  const isSingleUserPlan = maxUsers >= 0 && maxUsers <= 1;
  const CATEGORIES = buildCategories(t).filter(
    (category) => !(isSingleUserPlan && category.id === "family"),
  );
  // ─── Pin-as-default state ──────────────────────────────────────────────────
  const [isDefaultPage, setIsDefaultPage] = useState(
    () =>
      (localStorage.getItem("landingPage") || "Dashboard") ===
      "Getting Started",
  );
  const [pinConfirmed, setPinConfirmed] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});

  const toggleCategory = (id) =>
    setExpandedCategories((prev) => ({ ...prev, [id]: !prev[id] }));

  const handlePinAsDefault = () => {
    localStorage.setItem("landingPage", "Getting Started");
    UserSettingsAPI.updateSettings({
      defaultLandingPage: "Getting Started",
    }).catch(() => {});
    setIsDefaultPage(true);
    setPinConfirmed(true);
    setTimeout(() => setPinConfirmed(false), 2500);
    window.dispatchEvent(new Event("landingPageChanged"));
  };

  // Re-sync when another page changes the default
  useEffect(() => {
    const sync = () =>
      setIsDefaultPage(
        (localStorage.getItem("landingPage") || "Dashboard") ===
          "Getting Started",
      );
    window.addEventListener("landingPageChanged", sync);
    return () => window.removeEventListener("landingPageChanged", sync);
  }, []);

  // ─── Card click handler ────────────────────────────────────────────────────
  const handleCardClick = (item) => {
    setHeroContent(item.action);
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="getting-started-container">
      {/* Header */}
      <div className="getting-started-header">
        <div className="gs-header-text">
          <h1 className="main-title">{t("gs.header.title")}</h1>
          <p className="common-subtitle">{t("gs.header.subtitle")}</p>
        </div>
      </div>

      {/* Category sections */}
      {CATEGORIES.map((category) => {
        const isExpanded = !!expandedCategories[category.id];
        const hiddenCount = category.collapsible
          ? category.items.filter((i) => !i.primary).length
          : 0;
        const visibleItems =
          hiddenCount > 0 && !isExpanded
            ? category.items.filter((i) => i.primary)
            : category.items;
        const gridId = `gs-grid-${category.id}`;

        return (
          <div key={category.id} className="gs-category">
            <div className="gs-category-header">
              <h2>{category.title}</h2>
              <p>{category.subtitle}</p>
            </div>
            <div className="gs-category-grid" id={gridId}>
              {visibleItems.map((item) => {
                return (
                  <button
                    key={item.id}
                    className={`gs-card${item.recommended ? " gs-recommended" : ""}`}
                    onClick={() => handleCardClick(item)}
                  >
                    <div className="gs-card-icon">
                      <FontAwesomeIcon icon={item.icon} />
                    </div>
                    <div className="gs-card-body">
                      <h3>{item.label}</h3>
                      {/* <p
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(item.description, {
                          ALLOWED_TAGS: ["strong", "em", "br"],
                          ALLOWED_ATTR: [],
                        }),
                      }}
                    /> */}
                    </div>
                    <div className="gs-card-status">
                      <FontAwesomeIcon
                        icon={faArrowRight}
                        className="gs-arrow-icon"
                      />
                    </div>
                    {item.recommended && (
                      <span className="gs-recommended-badge">
                        {t("gs.recommended")}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            {category.id === "learn" && (
              <p className="gs-category-note">
                {t("gs.learn.simulationNote.before")}
                <button
                  type="button"
                  className="gs-inline-link"
                  onClick={() => setHeroContent("Simulation Profile")}
                >
                  {t("gs.learn.simulationNote.link")}
                </button>
                {t("gs.learn.simulationNote.after")}
              </p>
            )}
            {hiddenCount > 0 && (
              <button
                type="button"
                className="gs-more-toggle"
                aria-expanded={isExpanded}
                aria-controls={gridId}
                onClick={() => toggleCategory(category.id)}
              >
                <span className="gs-more-toggle-text">
                  <span className="gs-more-toggle-label">
                    {isExpanded
                      ? t("gs.more.hide")
                      : t("gs.more.show", { count: hiddenCount })}
                  </span>
                  {!isExpanded && category.moreHint && (
                    <span className="gs-more-toggle-hint">
                      {category.moreHint}
                    </span>
                  )}
                </span>
                <FontAwesomeIcon
                  icon={isExpanded ? faChevronUp : faChevronDown}
                  className="gs-more-toggle-icon"
                />
              </button>
            )}
          </div>
        );
      })}

      {/* Bottom section */}
      <div className="gs-footer">
        {/* ── Help & discovery ── */}
        <HelpOptionsFooter
          className="gs-footer-help"
          onContactSupport={() => setHeroContent("ContactSupport")}
        />

        {/* ── Install as app ── */}
        <div className="gs-footer-install">
          <InstallAppPanel />
        </div>
      </div>
    </div>
  );
}

export default GettingStarted;
